(function () {
  'use strict';

  if (window.__reefStickyAtcLoaded) return;
  window.__reefStickyAtcLoaded = true;

  var MOBILE = '(max-width: 749px)';
  var PAGE_CLASS = 'reef-sticky-atc-page';
  var OBSTRUCTED_CLASS = 'reef-obstructed';

  // Must match --reef-atc-h in reef-sticky-atc.css.
  var ATC_HEIGHT = 72;

  var bar = null;
  var titleSlot = null;
  var priceSlot = null;
  var button = null;

  var barShowing = false;
  var lastPrice = null;
  var lastLabel = null;
  var lastDisabled = null;
  var queued = false;
  var lastConsent = -1;
  var lastObstruction = -1;

  /* ======================================================================
     BOTTOM STACK - runs on every page

     Three things compete for the bottom edge of the viewport:

       1. Shopify's cookie consent banner - sitewide, any width, and the
          topmost of the three because its z-index is the highest.
       2. The sticky add-to-cart bar - mobile product pages only.
       3. The chat launcher and the Klaviyo teaser, which are anchored down
          there permanently and get buried by either of the above.

     These STACK rather than overlap: the consent banner sits on the floor,
     the add-to-cart bar sits on top of the banner, and the widgets clear
     both. An earlier version took the taller of the banner and the bar,
     assuming they overlapped - that left the buy button hidden underneath
     the banner, which is the worst possible thing to hide.

     Two properties are published rather than one:

       --reef-consent-h  the banner's height. Lifts the add-to-cart bar.
       --reef-obstruct   banner + bar. Lifts the widgets above both.

     Heights are measured, not hard-coded, because the consent banner is not
     a fixed size - 61px on desktop, 150px on mobile, and its copy wraps
     differently again at other widths.
     ====================================================================== */

  function bannerHeight() {
    var banner = document.getElementById('shopify-pc__banner');
    if (!banner) return 0;

    var styles = getComputedStyle(banner);
    if (styles.display === 'none' || styles.visibility === 'hidden') return 0;

    var rect = banner.getBoundingClientRect();
    if (rect.height === 0) return 0;

    // Only counts if it is actually sitting against the bottom edge. If the
    // banner is ever configured to a top or centred position in the admin,
    // it is not in anything's way and nothing should move for it.
    if (rect.bottom < window.innerHeight - 4) return 0;

    return Math.ceil(rect.height);
  }

  function updateStack() {
    var root = document.documentElement;

    var consent = bannerHeight();
    var obstruction = consent + (barShowing ? ATC_HEIGHT : 0);

    if (consent !== lastConsent) {
      lastConsent = consent;
      if (consent > 0) {
        root.style.setProperty('--reef-consent-h', consent + 'px');
      } else {
        root.style.removeProperty('--reef-consent-h');
      }
    }

    if (obstruction === lastObstruction) return;
    lastObstruction = obstruction;

    if (obstruction > 0) {
      root.style.setProperty('--reef-obstruct', obstruction + 'px');
      root.classList.add(OBSTRUCTED_CLASS);
    } else {
      // Removed entirely rather than set to zero. Leaving the rules matching
      // would keep !important overrides on the vendors' own positioning
      // across the whole site, which suppresses Klaviyo's entrance animation.
      root.style.removeProperty('--reef-obstruct');
      root.classList.remove(OBSTRUCTED_CLASS);
    }
  }

  var bannerObserver = null;
  var observedBanner = null;

  // The banner changes height when the viewport is narrow enough to wrap its
  // copy, so its size is watched as well as its existence.
  function observeBannerSize() {
    if (typeof ResizeObserver === 'undefined') return;

    var banner = document.getElementById('shopify-pc__banner');
    if (banner === observedBanner) return;

    if (bannerObserver) bannerObserver.disconnect();
    observedBanner = banner;
    if (!banner) return;

    bannerObserver = new ResizeObserver(updateStack);
    bannerObserver.observe(banner);
  }

  function watchStack() {
    updateStack();

    window.addEventListener('resize', updateStack, { passive: true });
    window.addEventListener('load', updateStack);

    if (typeof MutationObserver === 'undefined') return;

    // Shopify injects the banner after page load and removes it outright when
    // Accept or Decline is clicked, so watching body's children covers both
    // its arrival and its dismissal.
    new MutationObserver(function () {
      updateStack();
      observeBannerSize();
    }).observe(document.body, { childList: true });

    observeBannerSize();
  }

  /* ======================================================================
     STICKY ADD TO CART - product pages only
     ====================================================================== */

  // GATE 1 - the URL. Every Shopify product URL contains /products/,
  // including the /collections/x/products/y form.
  function onProductPage() {
    return /\/products\//.test(window.location.pathname);
  }

  // GATE 2 - the main product form, and only that.
  //
  // This previously fell back to a bare 'product-form button[name="add"]' if
  // the scoped lookup missed, which was wrong: collection and home pages
  // render a product-form inside every card for quick-add. The bar latched
  // onto the first card on the homepage and showed an empty title against
  // that card's price. Only '.product__info-container' identifies the real
  // product page form, so there is deliberately no fallback here.
  //
  // Never cache the result. Dawn re-renders the whole form through the
  // Section Rendering API on every variant change, so a held reference would
  // silently point at a detached node and the bar would stop working after
  // one variant click.
  function realButton() {
    return document.querySelector('.product__info-container product-form button[name="add"]');
  }

  function infoContainer() {
    return document.querySelector('.product__info-container');
  }

  function priceSource() {
    var scope = infoContainer() || document;
    return scope.querySelector('[data-reef-price]') || scope.querySelector('.price');
  }

  // Read the RENDERED price rather than cloning the node.
  //
  // reef-promo-bar.js rewrites '.price__regular .price-item--regular' inside
  // every '[data-reef-price]' element it finds. Cloning the price element
  // would put a second '[data-reef-price]' in the document, and applyPricing()
  // would then iterate the clone too - stamping its own output a second time
  // and duplicating the 'i' offers button. Reading text keeps this bar a
  // strictly passive mirror: it can display a wrong price only if the real
  // one is already wrong.
  function priceMarkup() {
    var source = priceSource();
    if (!source) return '';

    var now = source.querySelector('.reef-price-now');

    if (now) {
      var was = source.querySelector('.reef-price-was');
      var off = source.querySelector('.reef-price-off');

      return (
        (was ? '<s>' + was.textContent.trim() + '</s> ' : '') +
        '<strong>' + now.textContent.trim() + '</strong>' +
        (off ? ' <span class="reef-sticky-atc__off">' + off.textContent.trim() + '</span>' : '')
      );
    }

    var regular = source.querySelector('.price-item--regular');
    return regular ? '<strong>' + regular.textContent.trim() + '</strong>' : '';
  }

  function buttonLabel(real) {
    var span = real.querySelector('span:not(.visually-hidden)') || real.querySelector('span');
    var text = (span ? span.textContent : real.textContent) || '';
    return text.trim();
  }

  function drawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  }

  function modalOpen() {
    var modal = document.getElementById('ReefPromoModal');
    return !!(modal && modal.classList.contains('is-open'));
  }

  function build() {
    bar = document.createElement('div');
    bar.className = 'reef-sticky-atc';
    bar.setAttribute('data-reef-sticky-atc', '');
    bar.setAttribute('data-visible', 'false');
    bar.setAttribute('aria-hidden', 'true');

    bar.innerHTML =
      '<div class="reef-sticky-atc__inner">' +
      '<div class="reef-sticky-atc__info">' +
      '<span class="reef-sticky-atc__title"></span>' +
      '<span class="reef-sticky-atc__price"></span>' +
      '</div>' +
      '<button type="button" class="button reef-sticky-atc__button"></button>' +
      '</div>';

    // End of body, same reasoning as detachModal() in reef-promo-bar.js: keeps
    // it clear of any section's stacking context.
    document.body.appendChild(bar);

    titleSlot = bar.querySelector('.reef-sticky-atc__title');
    priceSlot = bar.querySelector('.reef-sticky-atc__price');
    button = bar.querySelector('.reef-sticky-atc__button');

    var heading = document.querySelector('.product__title h1, .product__title h2');
    if (heading) titleSlot.textContent = heading.textContent.trim();

    button.addEventListener('click', function () {
      var real = realButton();
      if (!real || real.disabled) return;
      // Click the real button rather than posting to /cart/add directly, so
      // product-form.js owns the request exactly as it does today - same
      // drawer open, same section re-render, same Regios recalculation.
      real.click();
    });
  }

  function syncContent() {
    var real = realButton();
    if (!real) return;

    var price = priceMarkup();
    if (price !== lastPrice) {
      priceSlot.innerHTML = price;
      lastPrice = price;
    }

    var label = buttonLabel(real);
    if (label && label !== lastLabel) {
      button.textContent = label;
      lastLabel = label;
    }

    // Mirrored rather than used to hide the bar. Dawn disables the real button
    // for the duration of the add-to-cart request, so hiding on disabled would
    // make the bar flicker away at the exact moment the shopper taps it.
    if (real.disabled !== lastDisabled) {
      button.disabled = real.disabled;
      lastDisabled = real.disabled;
    }
  }

  function syncVisibility() {
    var real = realButton();

    if (!real || !window.matchMedia(MOBILE).matches || drawerOpen() || modalOpen()) {
      setVisible(false);
      return;
    }

    var rect = real.getBoundingClientRect();
    setVisible(rect.bottom < 0 || rect.top > window.innerHeight);
  }

  function setVisible(show) {
    if (!bar) return;
    barShowing = show;
    bar.setAttribute('data-visible', show ? 'true' : 'false');
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    updateStack();
  }

  function update() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      syncContent();
      syncVisibility();
    });
  }

  function startBar() {
    build();
    update();

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('load', update);

    // Covers three sources of change with one observer: the promo bar
    // rewriting prices, Dawn swapping the form on variant change, and the
    // button flipping to a loading state mid-add.
    var container = infoContainer();
    if (container && typeof MutationObserver !== 'undefined') {
      new MutationObserver(update).observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class']
      });
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof MutationObserver !== 'undefined') {
      new MutationObserver(update).observe(drawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, update);
    }
  }

  function init() {
    // Sitewide: the consent banner appears on every template, so stack
    // tracking must not sit behind the product-page gate.
    watchStack();

    if (!onProductPage() || !realButton()) return;

    // PAGE_CLASS marks the template and nothing more - it reserves the page
    // padding for the bar.
    document.documentElement.classList.add(PAGE_CLASS);
    startBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
